import * as Sentry from "@sentry/react";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import useSpeechToText, { ResultType } from "react-hook-speech-to-text";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { useCreateMutation } from "../api/genericHooks";
import { QuestionStatusBadge } from "../components/Badges/QuestionStatusBadge";
import { QuestionNavButton } from "../components/Buttons/QuestionNavButton";
import { SpeechToTextButton } from "../components/Buttons/SpeechToTextButton";
import { BookContext } from "../contexts/BookContext";
import { selectUser } from "../store/slices/auth";
import { Locale } from "../types/models/Language";
import { Chapter, Question as QuestionModel, User } from "../types/models";

type AnswerData = {
  content: string;
  completed: boolean;
  book_id: number;
  question_id: number;
  user_id: number;
};
type SaveStatus = "saved" | "modified" | "saving";

// Resolves the route params, then renders the editor once everything exists.
// Keeping the early returns here lets QuestionEditor call its hooks
// unconditionally (rules of hooks).
export const Question = () => {
  const bookContext = useContext(BookContext);
  const { chapterNum, questionNum } = useParams();
  const book_id = bookContext?.book?.id;
  const user = useSelector(selectUser);

  if (!chapterNum || !questionNum || !book_id || !user) return null;

  const chapter = bookContext.chapters.find(
    (chapter) => chapter.number === parseInt(chapterNum)
  );
  if (!chapter) return null;

  const question = bookContext.questions.find(
    (question) =>
      question.number === parseInt(questionNum) &&
      question.chapter_id === chapter.id
  );
  if (!question) return null;

  return (
    <QuestionEditor
      chapter={chapter}
      question={question}
      book_id={book_id}
      user={user}
    />
  );
};

type QuestionEditorProps = {
  chapter: Chapter;
  question: QuestionModel;
  book_id: number;
  user: User;
};

const QuestionEditor = ({
  chapter,
  question,
  book_id,
  user,
}: QuestionEditorProps) => {
  const { t } = useTranslation();
  const local = localStorage.getItem("i18nextLng") as Locale;
  const bookContext = useContext(BookContext);
  const navigate = useNavigate();
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);

  // Speech to text
  const {
    error: speedToTextErrors,
    isRecording,
    results: speechToTextResults,
    startSpeechToText,
    stopSpeechToText,
  } = useSpeechToText({
    continuous: true,
    useLegacyResults: false,
    speechRecognitionProperties: {
      lang: local,
      interimResults: true,
    },
  });

  useEffect(() => {
    if (speedToTextErrors) {
      Sentry.captureException(speedToTextErrors);
    }
  }, [speedToTextErrors]);

  const chapterQuestions = bookContext.questions.filter(
    (question) => question.chapter_id === chapter.id
  );

  const answer = bookContext.answers.find(
    (answer) => answer.question_id === question.id && answer.user_id === user.id
  );

  const prevChapter =
    bookContext.chapters.find((chap) => chap.number === chapter.number - 1) ||
    chapter;

  const prevChapterQuestions = bookContext.questions.filter(
    (question) => question.chapter_id === prevChapter.id
  );

  const [completed, setCompleted] = useState(answer?.completed || false);

  // Form Methods
  const { register, handleSubmit, setValue, watch } = useForm<AnswerData>({
    defaultValues: {
      content: answer?.content,
      completed: answer?.completed || false,
      book_id: book_id,
      question_id: question.id,
      user_id: user.id,
    },
  });

  const content = watch("content");

  // Query methods
  const { mutateAsync: saveAnswerMutateAsync } = useCreateMutation("/answers");

  useEffect(() => {
    setCompleted(answer?.completed || false);
  }, [answer]);

  useEffect(() => {
    setValue("content", answer?.content || "");
    setValue("book_id", book_id);
    setValue("question_id", question.id);
    setValue("user_id", user.id);
    setValue("completed", completed);
  }, [completed, answer, question]);

  // Booleans
  const isFirstQuestion = question.number === 1;
  const isLastQuestion = question.number === chapterQuestions.length;
  const isFirstChapter = chapter.number === 1;
  const isLastQuestionAndChapter =
    isLastQuestion && chapter.number === bookContext.chapters.length;
  const isFirstQuestionAndChapter = isFirstQuestion && chapter.number === 1;
  const isAnswerContentChanged = answer?.content !== content;

  // Prev Chapter & Question
  const prevChapterNum = isFirstQuestion
    ? isFirstChapter
      ? 1
      : chapter.number - 1
    : chapter.number;

  const prevChapterLastQuestion =
    prevChapterQuestions[prevChapterQuestions.length - 1];

  const PrevChapterLastQuestionNum = isFirstChapter
    ? 1
    : prevChapterLastQuestion?.number;

  const prevQuestionNum = isFirstQuestion
    ? PrevChapterLastQuestionNum
    : question.number - 1;

  // Next Chapter & Question
  const nextChapterNum = isLastQuestion ? chapter.number + 1 : chapter.number;
  const nextQuestionNum = isLastQuestion ? 1 : question.number + 1;

  // Fonction pour suivre la position du curseur
  const handleTextareaClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    setCursorPosition(textarea.selectionStart);
  };

  // Également suivre la position lors de la sélection
  const handleTextareaSelect = (
    e: React.SyntheticEvent<HTMLTextAreaElement>
  ) => {
    const textarea = e.currentTarget;
    setCursorPosition(textarea.selectionStart);
  };

  // Fonction pour suivre le mouvement du curseur avec les touches
  const handleTextareaKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    setCursorPosition(textarea.selectionStart);
  };

  const autoSave = async () => {
    if (!content || !isAnswerContentChanged) return;

    setSaveStatus("saving");
    try {
      await saveAnswerMutateAsync({
        answer: {
          content,
          completed,
          book_id,
          question_id: question.id,
          user_id: user.id,
        },
      });
      setSaveStatus("saved");
      bookContext.refresh(); // facultatif
    } catch (error) {
      console.error("Auto-save failed", error);
      setSaveStatus("modified"); // en cas d'erreur, reste en modifié
    }
  };

  useEffect(() => {
    if (speechToTextResults && speechToTextResults.length > 0) {
      const lastResult = speechToTextResults[
        speechToTextResults.length - 1
      ] as ResultType;
      const lastResultText = lastResult.transcript;
      const currentContent = content || "";

      const trimmed = lastResultText.trim();
      let capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
      const punctuationMarks = [".", "!", "?"];
      const hasProperPunctuation = punctuationMarks.some((mark) =>
        capitalized.endsWith(mark)
      );

      // Add a period if no punctuation exists
      if (!hasProperPunctuation) {
        capitalized += ".";
      }

      // Insérer le texte à la position du curseur si disponible
      if (cursorPosition !== null) {
        const beforeCursor = currentContent.substring(0, cursorPosition);
        const afterCursor = currentContent.substring(cursorPosition);
        const newContent = beforeCursor + " " + capitalized + afterCursor;
        setValue("content", newContent);

        // Mettre à jour la position du curseur après l'insertion
        const newPosition = cursorPosition + capitalized.length + 1; // +1 pour l'espace
        setCursorPosition(newPosition);

        // Mettre le focus sur le textarea et positionner le curseur
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPosition, newPosition);
        }
      } else {
        // Fallback: ajouter à la fin si pas de position de curseur
        const newContent = currentContent + " " + capitalized;
        setValue("content", newContent);
      }

      autoSave();
    }
  }, [speechToTextResults]);

  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    if (answer?.content !== content) {
      setSaveStatus("modified");

      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 2000);
    } else {
      setSaveStatus("saved");
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [content]);

  const onSubmit = (data: AnswerData) => {
    stopSpeechToText();
    saveAnswerMutateAsync({ answer: data }).then(() => {
      toast.success(t("question.answerSaved"));
      if (isLastQuestionAndChapter) {
        navigate("/dashboard");
      }
      bookContext.refresh();
    });
  };

  const setCompletedAndSubmit = (completedChecked: boolean) => {
    setCompleted(completedChecked);
    setValue("completed", completedChecked);
    handleSubmit(onSubmit)();
  };

  const onClickNavigateButton = () => {
    stopSpeechToText();
    setCompletedAndSubmit(true);
  };

  // Obtenir la référence du textarea de react-hook-form
  const { ref, ...rest } = register("content");

  return (
    <div className="px-3 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-8 flex flex-1">
      <form
        className="bg-white flex flex-1 flex-col justify-between shadow-md rounded"
        onSubmit={(e) => {
          e.preventDefault();
          setCompletedAndSubmit(true);
        }}
      >
        <div>
          <div className="flex flex-col xl:flex-row xl:items-center p-3 sm:p-4">
            <div className="flex flex-1 flex-col xl:flex-row xl:items-center">
              <h1 className="text-lg sm:text-xl font-bold text-slate-700 w-max-3/4 flex flex-col sm:flex-row sm:items-center">
                <span className="mb-2 sm:mb-0">
                  {local && question.content[local]}
                </span>
                <div className="sm:ml-3">
                  <QuestionStatusBadge answer={answer} />
                </div>
              </h1>
            </div>
            <div className="flex flex-col items-start xl:items-end justify-center py-2 mt-4 xl:mt-0">
              <div className="flex flex-row items-center space-y-2 sm:space-y-0 space-x-3 w-full sm:w-auto">
                {saveStatus === "saved" && (
                  <span className="text-xs text-herb-600">
                    {t("question.saved")}
                  </span>
                )}
                {saveStatus === "modified" && (
                  <span className="text-xs text-sand-600">
                    {t("question.modified")}
                  </span>
                )}
                <div className="flex space-x-2 sm:space-x-3">
                  <SpeechToTextButton
                    isRecording={isRecording}
                    stopSpeechToText={stopSpeechToText}
                    startSpeechToText={startSpeechToText}
                  />
                  <button
                    className="flex bg-rust-50 text-white border hover:bg-rust-10 hover:text-rust-50 font-light py-2 px-3 sm:px-4 rounded text-sm sm:text-base"
                    type="submit"
                  >
                    {t("question.save")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col flex-1 p-3 sm:p-4">
          <textarea
            className="border flex-1 border-delicate-300 rounded p-2 outline-none text-nunito text-sm sm:text-base"
            placeholder={t("question.writeYourAnswer")}
            maxLength={10000}
            rows={10}
            ref={(e) => {
              ref(e);
              textareaRef.current = e;
            }}
            onClick={handleTextareaClick}
            onSelect={handleTextareaSelect}
            onKeyUp={handleTextareaKeyUp}
            {...rest}
          />
          <div className="flex flex-row justify-end mt-4">
            <p className="text-slate-300 text-xs sm:text-sm">
              {t("question.answerLength", { length: content?.length })}
            </p>
          </div>
        </div>

        <div className="flex flex-row justify-between px-3 sm:px-4 py-3 mt-4 gap-3 sm:gap-0">
          <div>
            {!isFirstQuestionAndChapter && prevQuestionNum && (
              <QuestionNavButton
                onClick={
                  isAnswerContentChanged || answer?.completed !== completed
                    ? () => onClickNavigateButton()
                    : () => stopSpeechToText()
                }
                mode="prev"
                nextPrevChapterNum={prevChapterNum}
                nextPrevQuestionNum={prevQuestionNum}
              />
            )}
          </div>
          <div>
            {isLastQuestionAndChapter ? (
              <button
                onClick={() => onClickNavigateButton()}
                className="border-rust-50 border hover:bg-rust-10 text-rust-50 hover:text-rust-300 font-light py-2 px-3 sm:px-4 rounded text-sm sm:text-base"
                type="button"
              >
                <p className="flex">
                  {t("question.navButtons.returnToDashboard")}
                </p>
              </button>
            ) : (
              <QuestionNavButton
                onClick={
                  isAnswerContentChanged || answer?.completed !== completed
                    ? () => onClickNavigateButton()
                    : () => stopSpeechToText()
                }
                mode="next"
                nextPrevChapterNum={nextChapterNum}
                nextPrevQuestionNum={nextQuestionNum}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
